import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useEffect, useRef, useState } from "react";
import { IDepartment } from "../../models/BaseModel";
import { baseUrl, Fetch, ILazyParams } from "../../shared/Fetch";
import { Search } from "../../shared/Search";
import { format } from "date-fns";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { IOpenForm } from "../user";

interface Iprops {
    setModal: (modal: IOpenForm) => void,
    toast: any,
    reload: boolean,
    reloadData: () => void,
    hasPermission: (pageName: string, featureName?: string) => boolean
}

export const Table = (props: Iprops) => {
    const [tableData, setTableData] = useState({ data: [], count: 0, loading: false } as { data: any[], count: number, loading: boolean });
    const [text, setText] = useState<string | null>(null);
    const dt = useRef<any>(null);
    const [lazyParams, setLazyParams] = useState<ILazyParams>({
        first: 0,
        rows: 25,
        page: 1,
        sortField: 'id',
        sortOrder: -1
    });

    // Helper: Group array into chunks of N
    const chunkArray = (array: any[], size: number) => {
        const chunked = [];
        for (let i = 0; i < array.length; i += size) {
            chunked.push(array.slice(i, i + size));
        }
        return chunked;
    };

    useEffect(() => {
        loadTableData();
    }, [text, lazyParams, props.reload]);

    const onPage = (event: any) => {
        delete event.filters;
        delete event.multiSortMeta;
        setLazyParams(event);
    };

    const onSort = (event: any) => {
        delete event.filters;
        delete event.multiSortMeta;
        setLazyParams(event);
    };

    const exportCSV = () => {
        dt.current.exportCSV();
    };

    const loadTableData = async () => {
        setTableData((prev) => ({ ...prev, loading: true }));
        const response = await Fetch("Gallery", "GET", null, `lazyParams=${JSON.stringify(lazyParams)}${text ? '&text=' + text : ''}`, props.toast);
        if (response) {
            const data: { image: any[], count: number } = await response.json();
            const grouped = chunkArray(data.image, 3); // group into 3 per row
            setTableData({ data: grouped, count: data.count, loading: false });
        } else {
            setTableData((prev) => ({ ...prev, loading: false }));
        }
    };

    const openNew = () => {
        props.setModal({ open: true, singleData: {} });
    };

    const header = (
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-4">
            <div className="flex gap-4 w-full">
                <Button
                    style={{ width: '80px', height: '30px' }}
                    label="New"
                    icon="pi pi-plus"
                    className="p-button-sm p-button-rounded p-button-success"
                    onClick={openNew}
                />
                <div className="w-full flex align-items-center justify-content-center">
                    <h4 style={{ margin: 0, textTransform: 'uppercase', fontFamily: 'Rancho', letterSpacing: 1 }}>Image Gallery</h4>
                </div>
            </div>
            <div className="flex gap-4">
                <div className="p-input-icon-left">
                    <Search setSearch={setText} />
                </div>
                <Button
                    style={{ width: '80px', height: '30px' }}
                    label="Export"
                    icon="pi pi-upload"
                    className="p-button-sm p-button-rounded p-button-help"
                    onClick={exportCSV}
                />
            </div>
        </div>
    );

    const deleteImage = async (id: number) => {
        confirmDialog({
            message: "Are you sure you want to delete this image?",
            header: "Confirmation",
            icon: "pi pi-exclamation-triangle",
            acceptClassName: "p-button-danger",
            accept: async () => {
                const response = await Fetch(
                    `Gallery/DeleteImage/${id}`, // send id in route
                    "DELETE",
                    null,
                    null,
                    props.toast
                );
                if (response) {
                    props.toast.current.show({
                        severity: "success",
                        summary: "Deleted",
                        detail: "Image deleted successfully",
                        life: 3000,
                    });
                    props.reloadData();
                }
            }
        });
    };

    // Render each row's 3 images
    const imageGroupBody = (rowData: any[]) => {
        return (
            <div style={{ display: "flex", gap: "20px", width: "100%" }}>
                {rowData.map((item, idx) => (
                    <div
                        key={idx}
                        style={{
                            flex: 1,
                            border: "1px solid #ddd",
                            borderRadius: "8px",
                            overflow: "hidden",
                            boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                            position: "relative"
                        }}
                    >
                        {/* Delete Button (top-right corner) */}
                        <Button
                            icon="pi pi-trash"
                            className="p-button-rounded p-button-danger p-button-sm"
                            style={{
                                position: "absolute",
                                top: "8px",
                                right: "8px",
                                zIndex: 1
                            }}
                            onClick={() => deleteImage(item.id)}
                            tooltip="Delete Image"
                        />

                        <img
                            src={`${baseUrl}Gallery/streamImage?fileName=${item.imageFileName}`}
                            alt="Gallery"
                            style={{
                                width: "100%",
                                height: "250px",
                                objectFit: "cover"
                            }}
                        />
                    </div>
                ))}
            </div>
        );
    };

    return (
        <>
            <DataTable
                style={{ border: '1px solid rgba(105,105,105,0.17)', borderTopWidth: "0" }}
                ref={dt}
                size="small"
                stripedRows
                scrollable
                scrollHeight="75vh"
                exportFilename={format(new Date(), 'dd-MM-yyyy hh_mm_ss a').toUpperCase()}
                value={tableData.data}
                lazy
                dataKey="id"
                paginator
                first={lazyParams.first}
                rows={lazyParams.rows}
                rowsPerPageOptions={[25, 50, 100, 200]}
                totalRecords={tableData.count}
                onPage={onPage}
                onSort={onSort}
                sortField={lazyParams.sortField}
                sortOrder={lazyParams.sortOrder}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                header={header}
                loading={tableData.loading}
            >
                <Column body={imageGroupBody} header="Images" />
            </DataTable>
            <ConfirmDialog />
        </>
    );
};
